import { useTranslation } from "react-i18next"

import { useMutation as useApolloMutation } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { MutationSchema, MutationTypename } from "shared/api/types"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ClarificationRequest } from "shared/types/__generated__/graphql"
import {
  ClarificationRequestSchema,
  CreateClarificationRequestInputSchema
} from "shared/types/__generated__/validation"

import {
  CreateClarificationRequestDocument,
  CreateClarificationRequestMutation,
  CreateClarificationRequestMutationVariables
} from "entities/clarification-requests/graphql/mutations.generated"
import { GetClarificationRequestDocument } from "entities/clarification-requests/graphql/queries.generated"
import z from "zod"

import { getQueryDocumentByResourceType } from "../lib/getQueryDocumentByResourceType"
import { updateResourceHasClarificationRequest } from "../lib/updateResourceHasClarificationRequest"
import { ClarificationRequestResourceType } from "../types/ClarificationRequest"

const CreateClarificationRequestVariablesSchema = z.object({
  resourceType: z.string(),
  id: z.string(),
  input: CreateClarificationRequestInputSchema()
})

export function useCreateClarificationRequest() {
  const { t } = useTranslation()
  const { userInfo } = useAuth()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t(
        "Request.ClarificationRequest.CREATE_CLARIFICATION_REQUEST.Start"
      ),
      success: t(
        "Request.ClarificationRequest.CREATE_CLARIFICATION_REQUEST.Success"
      ),
      failure: t(
        "Request.ClarificationRequest.CREATE_CLARIFICATION_REQUEST.Failure"
      )
    }
  })

  const [apolloMutate, rest] = useApolloMutation<
    CreateClarificationRequestMutation,
    CreateClarificationRequestMutationVariables
  >(CreateClarificationRequestDocument)

  const createClarificationRequest = useEvent(
    ({
      resourceType,
      id,
      content
    }: {
      resourceType: ClarificationRequestResourceType
      id: string
      content: string
    }) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()

      return callMutation(apolloMutate, {
        variables: { resourceType, id, input: { content } },
        transform: data => data.clarificationRequest as ClarificationRequest,
        schemas: {
          input: CreateClarificationRequestVariablesSchema,
          response: MutationSchema.extend({
            clarificationRequest: ClarificationRequestSchema()
          })
        },
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            clarificationRequest: {
              __typename: "ClarificationRequest" as const,
              id: `${resourceType}:${id}`,
              content,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              lastUpdatedBy:
                userInfo?.id && userInfo?.email
                  ? {
                      __typename: "User" as const,
                      id: userInfo.id,
                      name: userInfo.name ?? null,
                      email: userInfo.email,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    }
                  : null
            }
          },
          update: cache => {
            updateResourceHasClarificationRequest(resourceType, id, true, cache)
          },
          refetchQueries: result => {
            if (result.errors) {
              return [
                {
                  query: GetClarificationRequestDocument,
                  variables: { id, resourceType }
                },
                {
                  query: getQueryDocumentByResourceType(resourceType),
                  variables: { id }
                }
              ]
            }
            return []
          },
          onCompleted: () => {
            raiseSuccessToast()
          },
          onError: () => {
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [createClarificationRequest, rest] as [
    typeof createClarificationRequest,
    typeof rest
  ]
}
